const TOS = () => {
    return (
        <div className="web-page">
            <div className="web-section web-section-content" id="terms-of-service">
                <div className="web-section__container">
                    <div className="policy-container">
                        <div className="policy-header">
                            <h1 className="policy-title">Terms of Service</h1>
                            <p className="policy-effective-date">Effective Date: January 1, 2025</p>
                        </div>

                        <div className="policy-content">
                            <div className="policy-intro">
                                <p>Welcome to SponsorDB. By using our website and services, you agree to comply with and be bound by the following terms and conditions. Please read these terms carefully before using our website and services.</p>
                            </div>

                            <div className="policy-section">
                                <h3>1. Acceptance of Terms</h3>
                                <p>By accessing or using SponsorDB, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services. These terms apply to all users of the service, including without limitation users who are browsers, vendors, customers, merchants, and/or contributors of content.</p>
                            </div>

                            <div className="policy-section">
                                <h3>2. Description of Service</h3>
                                <p>SponsorDB provides access to a comprehensive database of newsletter sponsors and related information. Our service includes:</p>
                                <ul>
                                    <li>Access to sponsor contact information and details</li>
                                    <li>Filtering and search capabilities</li>
                                    <li>Regular database updates</li>
                                    <li>Customer support and assistance</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>3. User Accounts & Registration</h3>
                                <p>To access our services, you must:</p>
                                <ul>
                                    <li>Provide a valid email address</li>
                                    <li>Create a secure password</li>
                                    <li>Maintain accurate account information</li>
                                    <li>Notify us immediately of any unauthorized access</li>
                                </ul>
                                <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
                            </div>

                            <div className="policy-section">
                                <h3>4. Acceptable Use Policy</h3>
                                <p>You agree to use our services only for lawful purposes and in accordance with these Terms. You must not:</p>
                                <ul>
                                    <li>Use the service for any illegal or unauthorized purpose</li>
                                    <li>Attempt to gain unauthorized access to our systems</li>
                                    <li>Interfere with or disrupt the service</li>
                                    <li>Share your account credentials with others</li>
                                    <li>Use automated tools to access the service excessively</li>
                                    <li>Attempt to reverse engineer or copy our database</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>5. Email Communications & Opt-In Policy</h3>
                                <p>We respect your inbox and follow strict opt-in policies:</p>
                                <ul>
                                    <li><strong>Account Communications:</strong> Essential service notifications (password resets, account updates, payment confirmations)</li>
                                    <li><strong>Marketing Communications:</strong> Only sent with your explicit opt-in consent</li>
                                    <li><strong>Newsletter Updates:</strong> Database updates and new features (opt-in only)</li>
                                    <li><strong>Unsubscribe Rights:</strong> You can unsubscribe from marketing emails at any time</li>
                                </ul>
                                <p className="policy-note">By creating an account, you consent to receive essential account-related emails. Marketing emails require separate opt-in consent.</p>
                            </div>

                            <div className="policy-section">
                                <h3>6. Payment Terms</h3>
                                <p>Our service is a monthly subscription of $20/month:</p>
                                <ul>
                                    <li><strong>Payment Processing:</strong> Payments are processed securely through Stripe</li>
                                    <li><strong>Billing:</strong> Your payment method is charged $20 immediately upon subscribing</li>
                                    <li><strong>Recurring Billing:</strong> Subscriptions renew automatically each month until cancelled</li>
                                    <li><strong>Cancellation:</strong> Cancel anytime from your profile; access continues until the end of the paid period</li>
                                    <li><strong>Refund Policy:</strong> No refunds for partial billing periods</li>
                                    <li><strong>Legacy Purchases:</strong> Lifetime access purchased under our former one-time payment model remains honored</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>7. Intellectual Property Rights</h3>
                                <p>All content and materials available through our service are protected by intellectual property rights:</p>
                                <ul>
                                    <li><strong>Our Content:</strong> Website design, software, and original content belong to SponsorDB</li>
                                    <li><strong>Database Access:</strong> You may use sponsor information for legitimate business purposes</li>
                                    <li><strong>Restrictions:</strong> You may not copy, distribute, or resell our database</li>
                                    <li><strong>Attribution:</strong> Sponsor information should be used responsibly and ethically</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>8. Privacy & Data Protection</h3>
                                <p>Your privacy is important to us. Our data practices are governed by our Privacy Policy:</p>
                                <ul>
                                    <li>We collect minimal personal information necessary for service provision</li>
                                    <li>We do not sell or rent your personal information</li>
                                    <li>We implement appropriate security measures</li>
                                    <li>You have rights to access, correct, and delete your data</li>
                                </ul>
                                <p>Please review our <a href="/privacy-policy">Privacy Policy</a> for complete details.</p>
                            </div>

                            <div className="policy-section">
                                <h3>9. Service Availability & Updates</h3>
                                <p>We strive to maintain high service availability but cannot guarantee uninterrupted access:</p>
                                <ul>
                                    <li><strong>Maintenance:</strong> Scheduled maintenance may temporarily affect service</li>
                                    <li><strong>Updates:</strong> We regularly update our database and platform</li>
                                    <li><strong>Third-Party Dependencies:</strong> Service may be affected by third-party providers</li>
                                    <li><strong>Force Majeure:</strong> Events beyond our control may impact service</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>10. Limitation of Liability</h3>
                                <p>To the maximum extent permitted by law, SponsorDB shall not be liable for:</p>
                                <ul>
                                    <li>Indirect, incidental, or consequential damages</li>
                                    <li>Loss of profits, data, or business opportunities</li>
                                    <li>Damages resulting from use of sponsor information</li>
                                    <li>Service interruptions or data loss</li>
                                </ul>
                                <p>Our total liability shall not exceed the amount you paid for the service.</p>
                            </div>

                            <div className="policy-section">
                                <h3>11. Disclaimers</h3>
                                <p>Our service is provided "as is" without warranties of any kind:</p>
                                <ul>
                                    <li>We do not guarantee the accuracy of sponsor information</li>
                                    <li>We are not responsible for sponsor responses or outcomes</li>
                                    <li>We do not guarantee specific business results</li>
                                    <li>Service availability is not guaranteed</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>12. Termination</h3>
                                <p>We may terminate or suspend your account at any time for:</p>
                                <ul>
                                    <li>Violation of these Terms of Service</li>
                                    <li>Fraudulent or illegal activity</li>
                                    <li>Abuse of our services</li>
                                    <li>Non-payment of fees</li>
                                </ul>
                                <p>Upon termination, your access to the service will cease immediately.</p>
                            </div>

                            <div className="policy-section">
                                <h3>13. Changes to Terms</h3>
                                <p>We reserve the right to modify these terms at any time:</p>
                                <ul>
                                    <li>Changes will be posted on this page</li>
                                    <li>We'll update the effective date</li>
                                    <li>Significant changes will be communicated via email</li>
                                    <li>Continued use constitutes acceptance of changes</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>14. Governing Law & Disputes</h3>
                                <p>These Terms shall be governed by the laws of the State of Oklahoma:</p>
                                <ul>
                                    <li>Any disputes shall be resolved in Oklahoma courts</li>
                                    <li>We encourage resolution through direct communication</li>
                                    <li>Class action waivers may apply</li>
                                </ul>
                            </div>

                            <div className="policy-section">
                                <h3>15. Contact Information</h3>
                                <p>If you have questions about these Terms of Service, please contact us:</p>
                                <div className="policy-contact">
                                    <p><strong>Email:</strong> <a href="mailto:info@sponsor-db.com">info@sponsor-db.com</a></p>
                                    <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
                                    <p><strong>Business Hours:</strong> Monday - Friday, 9 AM - 5 PM CST</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TOS;
